import React, { useMemo, useState, Suspense, useEffect } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import Cookies from "js-cookie";
import AddUser from "../../assets/AddUser.svg";
import { Spinner, Button, Toaster, Position, Intent, Tooltip } from "@blueprintjs/core";
import { FilledButton } from "../buttons-20250224T111359Z-001/buttons";
import "../manage_access/manage_access.css";
import ManageAccessDrawer from "../manage_access/ManageAccessDrawer";
import BlankPage from "../goals/BlankPage";

const AppToaster = Toaster.create({
    position: Position.TOP,
});

function colorFromString(str) {
    if (!str) return "#9ca3af";
    let h = 0;
    for (let i = 0; i < str.length; i++) {
        h = (h * 31 + str.charCodeAt(i)) >>> 0;
    }
    const hue = h % 360;
    return `hsl(${hue}deg 60% 55%)`;
}

export default function AccountSettingManageAccess({
    roles: rolesProp,
    users: usersProp,
    onRemoveRole,
    onUpdateRoleMembers,
    apiBase
}) {
    const [usersState, setUsersState] = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);

    const [fetchedRoles, setFetchedRoles] = useState(null);
    const [rolesLoading, setRolesLoading] = useState(false);
    const [newlyCreatedRoles, setNewlyCreatedRoles] = useState([]);

    const [drawerOpen, setDrawerOpen] = useState(false);

    useEffect(() => {
    }, [drawerOpen]);
    const [drawerMode, setDrawerMode] = useState("editRole");
    const [drawerInitial, setDrawerInitial] = useState({ name: "", actions: [] });
    const [editingRoleId, setEditingRoleId] = useState(null);
    const [saving, setSaving] = useState(false);
    const DEFAULT_AVATAR = "https://cdn-icons-png.flaticon.com/512/847/847969.png";

    const [membersDraftByRole, setMembersDraftByRole] = useState({});

    useEffect(() => {
        const base = apiBase || Cookies.get("apiEnv");
        if (!base) {
            if (!Array.isArray(rolesProp) || rolesProp.length === 0) setFetchedRoles([]);
            return;
        }

        let cancelled = false;
        const fetchRoles = async () => {
            setRolesLoading(true);
            try {
                const res = await axios.get(`${base}/roles/roles_list`);
                const payload = res?.data;
                const list = Array.isArray(payload) ? payload : (payload?.roles || payload?.data || []);

                if (!Array.isArray(list) || list.length === 0) {
                    if (!cancelled) setFetchedRoles([]);
                    return;
                }

                const mapped = list.map((r, idx) => {
                    const id = (r?.id ?? r?.role_id ?? r?.roleName ?? r?.role_name ?? `r_api_${idx}`);
                    const name = (r?.name || r?.role_name || r?.display_name || `Role ${idx + 1}`);

                    const memberIds = Array.isArray(r?.users)
                        ? r.users.map(user => String(user?.id ?? user))
                        : Array.isArray(r?.member_ids)
                            ? r.member_ids.map(id => String(id))
                            : Array.isArray(r?.memberIds)
                                ? r.memberIds.map(id => String(id))
                                : r?.members
                                    ? r.members.map(m => String(m.id?.toString?.() ?? m.id))
                                    : [];
                    const role_access = {};
                    if (Array.isArray(r?.role_access)) {
                        r.role_access.forEach(access => {
                            if (access.module && Array.isArray(access.access_types)) {
                                role_access[access.module] = access.access_types;
                            }
                        });
                    } else if (r?.role_access && typeof r.role_access === 'object') {
                        Object.assign(role_access, r.role_access);
                    }

                    const actions = r?.actions || r?.permissions || [];
                    const description = r?.description || r?.details || "";

                    return {
                        id: String(id),
                        name,
                        memberIds,
                        actions,
                        description,
                        role_access,
                        users: Array.isArray(r?.users) ? r.users : undefined,
                    };
                });

                const seen = new Set();
                const uniqueMapped = mapped?.map((m, i) => {
                    let key = m.id;
                    if (seen.has(key) || key === "undefined") {
                        key = `${String(m.id)}_${i}`;
                    }
                    seen.add(key);
                    return { ...m, id: String(key) };
                });

                if (!cancelled) {
                    setFetchedRoles(uniqueMapped);
                    setNewlyCreatedRoles(prev => {
                        const fetchedIds = new Set(uniqueMapped.map(r => r.id));
                        return prev.filter(r => !fetchedIds.has(r.id));
                    });
                }
            } catch (err) {
                if (!cancelled) setFetchedRoles([]);
            } finally {
                if (!cancelled) setRolesLoading(false);
            }
        };

        if (!Array.isArray(rolesProp) || rolesProp.length === 0) {
            fetchRoles();
        } else {
            setFetchedRoles(rolesProp.map((r, idx) => ({
                id: String(r.id ?? `r_prop_${idx}`),
                name: r.name || r.role_name || `Role ${idx + 1}`,
                memberIds: Array.isArray(r.memberIds) ? r.memberIds : (Array.isArray(r.member_ids) ? r.member_ids : []),
                actions: r.actions || [],
                description: r.description || "",
                role_access: r.role_access || {},
                users: Array.isArray(r.users) ? r.users : undefined
            })));
        }

        return () => { cancelled = true; };
    }, [apiBase, rolesProp]);

    const roleList = useMemo(() => {
        const byId = new Map();
        const addAll = (arr) => {
            if (!Array.isArray(arr)) return;
            arr.forEach((r) => {
                const id = String(r.id);
                const prev = byId.get(id) || {};
                byId.set(id, { ...prev, ...r });
            });
        };

        addAll(rolesProp);
        addAll(fetchedRoles);
        addAll(newlyCreatedRoles);

        return Array.from(byId.values());
    }, [rolesProp, fetchedRoles, newlyCreatedRoles]);

    useEffect(() => {
        setMembersDraftByRole(prev => {
            const next = { ...prev };
            roleList.forEach(r => {
                if (!Array.isArray(next[r.id])) next[r.id] = Array.isArray(r.memberIds) ? [...r.memberIds] : [];
            });
            return next;
        });
    }, [roleList]);

    const userList = useMemo(() => {
        const base = (Array.isArray(usersProp) && usersProp.length > 0 && usersProp.some(u => u.name))
            ? usersProp
            : usersState;
        const result = (Array.isArray(base) ? base : []).map((u) => ({
            ...u,
            id: String(u.id),
            name: u.name || u.full_name || "",
            email: u.email || "",
            avatarUrl: u.avatarUrl || u.image_url || u.image_file_name || u.avatar || null,
            color: u.color || colorFromString(String(u?.email ?? u?.id ?? u.id)),
            initials: u.initials || (u.name || u.full_name ? (u.name || u.full_name).split(" ").map(n => n[0]).slice(0, 2).join("").toUpperCase() : "U"),
        }));
        return result;
    }, [usersProp, usersState]);

    const setRoleMembersDraft = (roleId, ids) =>
        setMembersDraftByRole((prev) => ({ ...prev, [roleId]: ids }));

    const openAddDrawer = () => {
        setEditingRoleId(null);
        setDrawerInitial({ name: "", actions: [] });
        setDrawerMode("editRole");
        setDrawerOpen(true);
    };

    const openEditDrawer = (role) => {
        const maybeOpen = (resolvedRole) => {
            setEditingRoleId(resolvedRole.id);
            setDrawerInitial({ name: resolvedRole.name || "", actions: resolvedRole.actions || [] });
            setMembersDraftByRole((prev) => ({
                ...prev,
                [resolvedRole.id]: resolvedRole.memberIds ? [...resolvedRole.memberIds] : prev[resolvedRole.id] || []
            }));
            setDrawerMode("editRole");
            setDrawerOpen(true);
        };

        const hasRoleAccess = (r) => {
            const ra = r && r.role_access;
            if (!ra) return false;
            if (Array.isArray(ra)) return ra.length > 0;
            if (typeof ra === 'object') return Object.keys(ra).length > 0;
            return false;
        };

        if (hasRoleAccess(role)) {
            maybeOpen(role);
            return;
        }

        (async () => {
            try {
                const base = apiBase || Cookies.get("apiEnv");
                if (!base) { maybeOpen(role); return; }
                const res = await axios.get(`${base}/roles/roles_list`);
                const payload = res?.data;
                const list = Array.isArray(payload) ? payload : (payload?.roles || payload?.data || []);
                const mapped = list.map((r, idx) => {
                    const id = (r?.id ?? r?.role_id ?? r?.roleName ?? r?.role_name ?? `r_api_${idx}`);
                    const name = (r?.name || r?.role_name || r?.display_name || `Role ${idx + 1}`);
                    const memberIds = Array.isArray(r?.users)
                        ? r.users.map(user => String(user?.id ?? user))
                        : Array.isArray(r?.member_ids)
                            ? r.member_ids.map(id => String(id))
                            : Array.isArray(r?.memberIds)
                                ? r.memberIds.map(id => String(id))
                                : r?.members
                                    ? r.members.map(m => String(m.id?.toString?.() ?? m.id))
                                    : [];
                    const role_access = Array.isArray(r?.role_access) || (r?.role_access && typeof r.role_access === 'object')
                        ? r.role_access
                        : {};
                    const actions = r?.actions || r?.permissions || [];
                    const description = r?.description || r?.details || "";
                    return { id: String(id), name, memberIds, actions, description, role_access, users: Array.isArray(r?.users) ? r.users : undefined };
                });
                setFetchedRoles(mapped);
                const enriched = mapped.find(r => String(r.id) === String(role.id)) || role;
                maybeOpen(enriched);
            } catch (e) {
                maybeOpen(role);
            }
        })();
    };

    const openMembersDrawer = (role) => {
        setEditingRoleId(role.id);
        setMembersDraftByRole((prev) => ({
            ...prev,
            [role.id]: Array.isArray(role.memberIds) ? [...role.memberIds] : []
        }));
        setDrawerInitial({ name: role.name || "", actions: role.actions || [] });
        setDrawerMode("editMembers");
        setDrawerOpen(true);
    };

    function extractMessage(resp) {
        if (resp === null || resp === undefined) return null;
        if (typeof resp === "string") {
            try {
                const parsed = JSON.parse(resp);
                return extractMessage(parsed) || resp;
            } catch (e) {
                return resp;
            }
        }
        if (resp?.data !== undefined) return extractMessage(resp.data);
        if (typeof resp.message === "string" && resp.message.trim() !== "") return resp.message;
        if (typeof resp.msg === "string" && resp.msg.trim() !== "") return resp.msg;
        if (Array.isArray(resp.messages) && resp.messages.length) return resp.messages.join(", ");
        if (resp?.errors) {
            try {
                const firstErr = Object.values(resp.errors).flat()[0];
                if (firstErr) return String(firstErr);
            } catch (e) { }
        }
        try {
            const s = JSON.stringify(resp);
            if (s && s.length < 400) return s;
        } catch (e) { }
        return null;
    }

    const handleSaveRole = async ({ name, actions, apiResponse, isUpdate, roleId, users, role_access, updatedRoles, toastMessage }) => {
        setSaving(true);
        try {
            const msgFromApi = extractMessage(apiResponse);
            const msgFromUpdated = extractMessage(updatedRoles);
            const finalMsg = toastMessage || msgFromApi || msgFromUpdated || (isUpdate ? "Updated successfully" : "Role created successfully");

            if (isUpdate && roleId) {
                if (Array.isArray(updatedRoles) && updatedRoles.length > 0) {
                    setFetchedRoles(updatedRoles);
                } else if (Array.isArray(fetchedRoles)) {
                    setFetchedRoles(prev =>
                        prev.map(r => {
                            if (r.id !== roleId) return r;
                            const nextName = name ?? r.name;
                            const nextActions = actions ?? r.actions;
                            const nextMembers = Array.isArray(users) ? users : (r.memberIds || []);
                            const nextAccess = role_access ?? r.role_access ?? {};
                            return { ...r, name: nextName, actions: nextActions, memberIds: nextMembers, role_access: nextAccess };
                        })
                    );
                }

                setNewlyCreatedRoles(prev =>
                    prev.map(r => {
                        if (r.id !== roleId) return r;
                        const nextName = name ?? r.name;
                        const nextActions = actions ?? r.actions;
                        const nextMembers = Array.isArray(users) ? users : (r.memberIds || []);
                        const nextAccess = role_access ?? r.role_access ?? {};
                        return { ...r, name: nextName, actions: nextActions, memberIds: nextMembers, role_access: nextAccess };
                    })
                );

                setMembersDraftByRole(prev => ({ ...prev, [roleId]: Array.isArray(users) ? users : (prev[roleId] || []) }));

                onUpdateRoleMembers?.(roleId, Array.isArray(users) ? users : (membersDraftByRole[roleId] || []));
                setEditingRoleId(null);
                setDrawerOpen(false);

                AppToaster.show({ message: finalMsg, intent: Intent.SUCCESS });
                return;
            }

            const realId =
                roleId ||
                (Array.isArray(updatedRoles)
                    ? (updatedRoles.find(r => String(r.name).toLowerCase() === String(name).toLowerCase())?.id)
                    : null) ||
                (apiResponse?.id || apiResponse?.role_id || apiResponse?.data?.id || apiResponse?.data?.role_id || null);

            if (Array.isArray(updatedRoles) && updatedRoles.length > 0) {
                setFetchedRoles(updatedRoles);
            }

            if (realId) {
                setMembersDraftByRole(prev => ({
                    ...prev,
                    [realId]: Array.isArray(prev['new_role_temp']) ? prev['new_role_temp'] : (prev[realId] || [])
                }));
                onUpdateRoleMembers?.(realId, membersDraftByRole['new_role_temp'] || membersDraftByRole[realId] || []);
            }

            setDrawerOpen(false);

            AppToaster.show({ message: finalMsg || "Role created successfully", intent: Intent.SUCCESS });
        } catch (err) {
            let errMsg = "Something went wrong";
            try {
                if (err?.response) {
                    errMsg = extractMessage(err.response.data) || err.response.statusText || `Error ${err.response.status}`;
                } else if (err?.message) {
                    errMsg = err.message;
                } else {
                    errMsg = String(err);
                }
            } catch (e) {
                errMsg = "An unexpected error occurred";
            }
            AppToaster.show({ message: errMsg, intent: Intent.DANGER });
        } finally {
            setSaving(false);
        }
    };

    const initialsFor = (u) => u.initials || (u.name ? u.name.split(" ").map(n => n[0]).slice(0, 2).join("").toUpperCase() : "U");

    return (
        <div className="ma-page">
            <div className="ma-main">
                <div className="ma-table-wrap">
                    <div className="ma-toolbar">
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", margin: "10px 0px 10px 10px" }}>
                            <div />
                            <FilledButton text="+ Add New Role" onClick={openAddDrawer} />
                        </div>
                    </div>

                    {(rolesLoading || usersLoading) ? (
                        <div className="ma-spinner"><Spinner size={24} /></div>
                    ) : (
                        <>
                            {roleList.length === 0 ? (
                                <div className="push-30-t">
                                    <BlankPage
                                        title={"No Roles Yet"}
                                        description={"You haven't created any roles. Create a role to manage access and assign users."}
                                    />
                                </div>
                            ) : (
                                <table className="bp3-html-table bp3-html-table-bordered bp3-small ma-roles-table" style={{ border: "1px solid #e6ebf1", borderCollapse: "separate", width: "100%" }}>
                                    <thead style={{ backgroundColor: "rgba(189, 195, 199, 0.10)", color: "#4D4D4D" }}>
                                        <tr style={{ fontSize: "14px", lineHeight: "normal" }}>
                                            <th style={{ width: "30%", padding: "19px 7px", textAlign: "left" }}>Role</th>
                                            <th style={{ width: "70%", padding: "19px 14px", textAlign: "left" }}>Users</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {roleList?.map((r) => {
                                            const committedIds = r.memberIds || [];

                                            const selectedUsers = Array.isArray(r.users) && r.users.length
                                                ? r.users.map((u, idx) => {
                                                    const name = u?.full_name || u?.name || u?.email || "";
                                                    return {
                                                        id: String(u?.id ?? `u_role_${idx}`),
                                                        name,
                                                        email: u?.email || "",
                                                        avatarUrl: u?.image_url || u?.image_file_name || u?.avatar || null,
                                                        color: u?.color,
                                                        initials:
                                                            u?.initials ||
                                                            (name
                                                                ? name
                                                                    .split(" ")
                                                                    .map(n => n[0])
                                                                    .slice(0, 2)
                                                                    .join("")
                                                                    .toUpperCase()
                                                                : "U"),
                                                    };
                                                })
                                                : committedIds.map(id => userList.find(u => String(u.id) === String(id))).filter(Boolean);

                                            const displayUsers = selectedUsers;
                                            const maxVisible = 10;
                                            const remainingCount = Math.max(0, (displayUsers?.length || 0) - maxVisible);

                                            return (
                                                <tr key={r.id} style={{ verticalAlign: "middle" }}>
                                                    <td style={{ padding: 8 }}>
                                                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                                            <div style={{ display: "flex", flexDirection: "column" }}>
                                                                <Tooltip 
                                                                    content="Edit"
                                                                    position={Position.TOP}
                                                                    hoverOpenDelay={100}
                                                                    boundary="viewport"
                                                                    className="custom-bp-tooltip"
                                                                >
                                                                    <div
                                                                        role="button"
                                                                        tabIndex={0}
                                                                        onClick={(e) => { e.stopPropagation(); openEditDrawer(r); }}
                                                                        onKeyDown={(e) => {
                                                                            if (e.key === "Enter" || e.key === " ") {
                                                                                e.preventDefault();
                                                                                openEditDrawer(r);
                                                                            }
                                                                        }}
                                                                        onMouseEnter={(e) => (e.currentTarget.style.color = "#0000e1")}
                                                                        onMouseLeave={(e) => (e.currentTarget.style.color = "#000")}
                                                                        style={{
                                                                            cursor: "pointer",
                                                                            color: "#000",
                                                                            outline: "none",
                                                                        }}
                                                                        aria-label={`Edit role ${r.name}`}
                                                                    >
                                                                        {r.name}
                                                                    </div>
                                                                </Tooltip>
                                                            </div>
                                                        </div>
                                                    </td>

                                                    <td style={{ padding: 16 }}>
                                                        <div className="ma-members" style={{ display: "flex", alignItems: "center" }}>
                                                            {displayUsers && displayUsers.length > 0 ? (
                                                                <>
                                                                    {displayUsers.slice(0, maxVisible).map((u, index) => (
                                                                        <Tooltip
                                                                            key={u.id || index}
                                                                            content={(
                                                                                <div style={{ display: "block", textAlign: "left" }}>
                                                                                    <div style={{ fontWeight: 600 }}>{u.name || u.email || "Unknown"}</div>
                                                                                    {u.email ? (
                                                                                        <div style={{ opacity: 0.85 }}>{u.email}</div>
                                                                                    ) : null}
                                                                                </div>
                                                                            )}
                                                                            position={Position.TOP}
                                                                            hoverOpenDelay={100}
                                                                            boundary="viewport"
                                                                            className="custom-bp-tooltip"
                                                                        >
                                                                            <div className={`ma-avatar-chip ${index > 0 ? 'ma-overlap' : ''}`}>
                                                                                <div className="ma-avatar">
                                                                                    {u.avatarUrl ? (
                                                                                        <img
                                                                                            src={u.avatarUrl}
                                                                                            alt={u.name}
                                                                                            onError={(e) => {
                                                                                                e.currentTarget.onerror = null;
                                                                                                e.currentTarget.src = DEFAULT_AVATAR;
                                                                                            }}
                                                                                        />
                                                                                    ) : (
                                                                                        <div
                                                                                            className="ma-avatar-fallback"
                                                                                            style={{ background: u.color || "#9ca3af" }}
                                                                                        >
                                                                                            {u.initials || initialsFor(u)}
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        </Tooltip>
                                                                    ))}
                                                                    {remainingCount > 0 && (
                                                                        <Tooltip
                                                                            content={`${remainingCount} more members`}
                                                                            position={Position.TOP}
                                                                            hoverOpenDelay={100}
                                                                            boundary="viewport"
                                                                            className="custom-bp-tooltip"
                                                                        >
                                                                            <div
                                                                                className="ma-avatar-badge"
                                                                                style={{ marginLeft: 8, cursor: "default" }}
                                                                            >
                                                                                +{remainingCount}
                                                                            </div>
                                                                        </Tooltip>
                                                                    )}

                                                                </>
                                                            ) : (
                                                                <div className="ma-empty-members">No users assigned — add users or assign a team.</div>
                                                            )}

                                                            <Tooltip
                                                                content="Add Users & Teams"
                                                                position={Position.TOP}
                                                                hoverOpenDelay={100}
                                                                boundary="viewport"
                                                                className="custom-bp-tooltip"
                                                            >
                                                                <Button
                                                                    minimal
                                                                    small
                                                                    aria-label={`Manage users for ${r.name}`}
                                                                    className="ma-add-member-btn"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        openMembersDrawer(r);
                                                                    }}
                                                                    style={{ marginLeft: 8 }}
                                                                >
                                                                    <img
                                                                        src={AddUser}
                                                                        alt="team icon"
                                                                        style={{ width: 16, height: 16 }}
                                                                    />
                                                                </Button>
                                                            </Tooltip>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </>
                    )}
                </div>
            </div>

            <ManageAccessDrawer
                isOpen={drawerOpen}
                onClose={() => { setDrawerOpen(false); setEditingRoleId(null); }}
                onSubmit={handleSaveRole}
                isSaving={saving}
                title={editingRoleId ? (drawerMode === "editMembers" ? "Manage Users" : "Edit Role") : "Add Role"}
                initialName={drawerInitial.name}
                initialActions={drawerInitial.actions}
                users={userList}
                mode={drawerMode}
                selectedIds={editingRoleId ? (membersDraftByRole[editingRoleId] || roleList.find(r => r.id === editingRoleId)?.memberIds || []) : []}
                onMembersChange={(updatedIds) => {
                    if (editingRoleId) {
                        setRoleMembersDraft(editingRoleId, updatedIds);
                    } else {
                        const tempKey = 'new_role_temp';
                        setRoleMembersDraft(tempKey, updatedIds);
                    }
                }}
                apiBase={apiBase}
                editingRoleId={editingRoleId}
                existingRoleData={editingRoleId ? roleList.find(r => r.id === editingRoleId) : null}
            />
        </div>
    );
}

AccountSettingManageAccess.propTypes = {
    roles: PropTypes.array,
    users: PropTypes.array,
    onRemoveRole: PropTypes.func,
    onUpdateRoleMembers: PropTypes.func,
    apiBase: PropTypes.string
};
